import { useState } from "react"

import { Button, Checkbox, FormField, Input, MultiSelect } from "@coval/ui"

import { reportScopeCapacity } from "../../utils/reportScopeCapacity"
import type { Bootstrap, Scope } from "../../utils/reportTypes"

export function UploadedAgentSelection({
  scope,
  bootstrap,
  disabled,
  onChange,
}: {
  scope: Scope
  bootstrap: Bootstrap
  disabled: boolean
  onChange: (scope: Partial<Scope>) => void
}) {
  const [customValue, setCustomValue] = useState("")
  const capacity = reportScopeCapacity(scope, bootstrap.scope_limits)
  const selection = scope.upload_agent
  const field = selection?.metadata_key ?? ""
  const values = selection?.values ?? []
  const options = Array.from(
    new Set([...(bootstrap.upload_metadata?.fields[field] ?? []), ...values]),
  ).map((value) => ({ value, label: value }))
  const updateValues = (next: string[]) =>
    onChange({ upload_agent: { metadata_key: field, values: next } })

  return (
    <div className="space-y-3">
      <FormField
        label="Uploaded agent metadata field"
        description="Enter the metadata key that identifies agents in your uploads, for example agent_name. Leave blank to include all uploads."
      >
        {(ids) => (
          <>
            <Input
              {...ids}
              value={field}
              disabled={disabled}
              list="uploaded-agent-metadata-fields"
              placeholder="e.g. agent_name"
              onChange={(event) => {
                setCustomValue("")
                onChange({
                  upload_agent: event.target.value
                    ? { metadata_key: event.target.value, values: [] }
                    : undefined,
                })
              }}
            />
            <datalist id="uploaded-agent-metadata-fields">
              {Object.keys(bootstrap.upload_metadata?.fields ?? {}).map(
                (key) => (
                  <option key={key} value={key} />
                ),
              )}
            </datalist>
          </>
        )}
      </FormField>
      {field && (
        <>
          <FormField
            label="Uploaded agents"
            description={`Add exact metadata values for your uploaded agents. Select up to ${capacity.maxUploadValues} values with the current simulation choices.`}
          >
            {(ids) => (
              <MultiSelect
                aria-labelledby={ids["aria-labelledby"]}
                value={values}
                options={options}
                onValueChange={updateValues}
                disabled={disabled}
                searchable
                placeholder="All uploaded conversations"
                footer={
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={!values.length}
                      disabled={disabled}
                      onCheckedChange={() => updateValues([])}
                      aria-label="All uploaded conversations"
                    />
                    All uploaded conversations
                  </label>
                }
              />
            )}
          </FormField>
          <div className="flex gap-2">
            <Input
              aria-label="Exact uploaded agent metadata value"
              placeholder="Add an exact metadata value"
              value={customValue}
              disabled={disabled}
              onChange={(event) => setCustomValue(event.target.value)}
            />
            <Button
              variant="secondary"
              disabled={
                disabled || !customValue || values.includes(customValue)
              }
              onClick={() => {
                updateValues([...values, customValue])
                setCustomValue("")
              }}
            >
              Add
            </Button>
          </div>
          <p className="text-sm text-coval-text-muted">
            All uploads includes conversations missing this field. Selecting
            values includes only conversations with those exact values.
          </p>
        </>
      )}
    </div>
  )
}
